import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { invitationApi } from '../utils/api'
import AlertModal from '../components/AlertModal'
import ConfirmModal from '../components/ConfirmModal'

export default function AcceptInvitation() {
  const { id } = useParams()
  const navigate = useNavigate()
  const [invitation, setInvitation] = useState(null)
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState(null)
  const [accepted, setAccepted] = useState(false)
  const [confirmDeclineOpen, setConfirmDeclineOpen] = useState(false)
  const [alertState, setAlertState] = useState({
    open: false,
    type: 'info',
    title: '',
    message: '',
  })
  const closeAlert = () => setAlertState((prev) => ({ ...prev, open: false }))
  const closeConfirm = () => {
    if (!submitting) {
      setConfirmDeclineOpen(false)
    }
  }

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm()

  useEffect(() => {
    const loadInvitation = async () => {
      try {
        setLoading(true)
        const data = await invitationApi.getById(id)
        setInvitation(data)
      } catch (err) {
        setError(err.message)
      } finally {
        setLoading(false)
      }
    }
    loadInvitation()
  }, [id])

  const onAccept = async (formData) => {
    try {
      setSubmitting(true)
      setError(null)
      await invitationApi.accept(id, { name: formData.name })
      setAccepted(true)
      setAlertState({
        open: true,
        type: 'success',
        title: 'Invitation Accepted',
        message: 'Welcome to the box! Redirecting you now...',
      })
      
      setTimeout(() => {
        navigate(`/boxes/${invitation.box_id}`)
      }, 2000)
    } catch (err) {
      setAlertState({
        open: true,
        type: 'error',
        title: 'Unable to Accept Invitation',
        message: err.message || 'Please try again.',
      })
    } finally {
      setSubmitting(false)
    }
  }

  const onDecline = () => {
    setConfirmDeclineOpen(true)
  }

  const handleDeclineConfirm = async () => {
    try {
      setSubmitting(true)
      setError(null)
      await invitationApi.decline(id)
      setConfirmDeclineOpen(false)
      setAlertState({
        open: true,
        type: 'info',
        title: 'Invitation Declined',
        message: 'You will be redirected to the homepage.',
      })
      setTimeout(() => {
        navigate('/')
      }, 1500)
    } catch (err) {
      setAlertState({
        open: true,
        type: 'error',
        title: 'Unable to Decline Invitation',
        message: err.message || 'Please try again.',
      })
    } finally {
      setSubmitting(false)
    }
  }

  const modals = (
    <>
      <AlertModal
        open={alertState.open}
        type={alertState.type}
        title={alertState.title}
        message={alertState.message}
        onClose={closeAlert}
      />
      <ConfirmModal
        open={confirmDeclineOpen}
        onClose={closeConfirm}
        onConfirm={handleDeclineConfirm}
        loading={submitting}
        title="Decline Invitation?"
        message="Are you sure you want to decline this invitation? You can accept later if invited again."
        confirmLabel="Decline"
        cancelLabel="Keep Invitation"
      />
    </>
  )

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-gray-500">Loading invitation...</div>
      </div>
    )
  }

  if (error && !invitation) {
    return (
      <div className="max-w-md mx-auto mt-8 bg-red-50 p-4 rounded-md">
        <p className="text-red-800">Error loading invitation: {error}</p>
      </div>
    )
  }

  if (!invitation) {
    return (
      <div className="max-w-md mx-auto mt-8 bg-yellow-50 p-4 rounded-md">
        <p className="text-yellow-800">Invitation not found.</p>
      </div>
    )
  }

  if (invitation.status === 'accepted') {
    return (
      <div className="max-w-md mx-auto mt-8 bg-green-50 p-4 rounded-md">
        <p className="text-green-800">This invitation has already been accepted.</p>
        <button
          onClick={() => navigate(`/boxes/${invitation.box_id}`)}
          className="mt-4 text-primary-600 hover:text-primary-700"
        >
          View Box
        </button>
      </div>
    )
  }

  if (invitation.status === 'declined') {
    return (
      <div className="max-w-md mx-auto mt-8 bg-gray-50 p-4 rounded-md">
        <p className="text-gray-800">This invitation has been declined.</p>
      </div>
    )
  }

  if (accepted) {
    return (
      <>
        {modals}
        <div className="max-w-md mx-auto mt-8 bg-green-50 p-6 rounded-lg shadow-md">
          <h2 className="text-xl font-semibold text-green-800 mb-2">Welcome!</h2>
          <p className="text-green-700">
            You&apos;ve successfully joined the box. Redirecting to the box page...
          </p>
        </div>
      </>
    )
  }

  return (
    <>
      {modals}
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white shadow-2xl rounded-3xl overflow-hidden border border-gray-100">
        <div className="bg-primary-600 px-6 py-8 text-white">
          <h1 className="text-3xl font-bold">You&apos;re Invited!</h1>
          <p className="mt-2 text-primary-100">
            Join a rotating savings box
          </p>
        </div>

        <div className="px-6 py-8">
          <div className="mb-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              {invitation.box?.name}
            </h2>
            <p className="text-gray-700 mb-4">
              {invitation.box?.description}
            </p>
            
            <div className="bg-gray-50 rounded-lg p-4 space-y-2">
              <div className="flex justify-between">
                <span className="text-gray-600">Monthly Contribution:</span>
                <span className="font-medium text-gray-900">
                  ${invitation.box?.contribution_amount}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Cycle Duration:</span>
                <span className="font-medium text-gray-900">
                  {invitation.box?.cycle_duration_months} months
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Current Members:</span>
                <span className="font-medium text-gray-900">
                  {invitation.box?.member_count ?? 0}
                </span>
              </div>
              {invitation.box?.start_date && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Start Date:</span>
                  <span className="font-medium text-gray-900">
                    {new Date(invitation.box.start_date).toLocaleDateString()}
                  </span>
                </div>
              )}
            </div>
          </div>

          {error && (
            <div className="mb-6 bg-red-50 p-4 rounded-md">
              <p className="text-red-800">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit(onAccept)} className="space-y-6">
            <div>
              <label htmlFor="name" className="block text-base font-semibold text-gray-900 mb-2">
                Your Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="name"
                {...register('name', { required: 'Name is required' })}
                defaultValue={invitation.name}
                className="block w-full px-4 py-3 text-base border border-gray-200 rounded-2xl shadow-sm bg-white text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                placeholder="Enter your full name"
              />
              {errors.name && (
                <p className="mt-2 text-sm text-red-600">{errors.name.message}</p>
              )}
            </div>

            <div className="flex space-x-4 pt-4">
              <button
                type="submit"
                disabled={submitting}
                className="flex-1 px-6 py-3 border border-transparent rounded-full shadow-sm text-base font-semibold text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {submitting ? 'Accepting...' : 'Accept Invitation'}
              </button>
              <button
                type="button"
                onClick={onDecline}
                disabled={submitting}
                className="flex-1 px-6 py-3 border border-gray-200 rounded-full shadow-sm text-base font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Decline
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
    </>
  )
}
